<template>
  <div class="page-root">
    <main id="ai-tools" class="main-content px-4 py-1">
      <!-- 生成面板：支持 SSR -->
      <GenerativePanel
        :toolName="toolName"
        :toolTitle="toolTitle"
        :sampleConfig="sampleConfig"
        :toolPageConfig="toolPageConfig"
      />

      <!-- 导言部分：静态展示组件（会被 SSR / SSG 输出到页面源代码） -->
      <!-- 如果是 JSON 对象，使用 BaseIntroSection 组件 -->
      <BaseIntroSection
        v-if="hasPageStaticConfigObject"
        v-bind="parsedPageStaticConfig"
      />
      <!-- 如果是 HTML 字符串，直接渲染 HTML（支持 SSR） -->
      <div
        v-else-if="hasHtmlPageStaticConfig"
        ref="htmlContentRef"
        class="text-white py-6"
        v-html="htmlPageStaticConfig"
      />
    </main>
  </div>
</template>

<script setup>
import { provide, computed, ref, onMounted, onUnmounted, nextTick } from "vue";
import GenerativePanel from "@/components/ai-tools/GenerativePanel.vue";
import BaseIntroSection from "@/components/ai-tools/BaseIntroSection.vue";

const props = defineProps({
  toolName: {
    type: String,
    required: true,
  },
  toolTitle: {
    type: String,
    required: true,
  },
  sampleConfig: {
    type: Object,
  },
  seoUrl: {
    type: String,
    required: true,
  },
  seoTitle: {
    type: String,
    required: true,
  },
  seoDescription: {
    type: String,
    required: true,
  },
  pageStaticConfig: {
    type: [Object, String],
    required: true,
  },
  toolPageConfig: {
    type: Object,
    required: true,
  },
});

// 辅助函数：尝试解析 JSON 对象
const tryParseJsonObject = (value) => {
  if (!value) return null;

  // 如果已经是对象，直接返回
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return value;
  }

  // 如果是字符串，尝试解析为 JSON
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // 检查是否以 { 或 [ 开头（可能是 JSON）
    if (trimmed[0] === "{" || trimmed[0] === "[") {
      try {
        const parsed = JSON.parse(trimmed);
        // 如果解析成功且是对象（不是数组），则返回
        if (
          typeof parsed === "object" &&
          parsed !== null &&
          !Array.isArray(parsed)
        ) {
          return parsed;
        }
      } catch {
        // 解析失败，不是有效的 JSON
      }
    }
  }

  return null;
};

// 检测 pageStaticConfig 是否为 JSON 对象
const isPageStaticConfigObject = computed(() => {
  return tryParseJsonObject(props.pageStaticConfig) !== null;
});

// 解析后的 pageStaticConfig 对象（用于 BaseIntroSection）
const parsedPageStaticConfig = computed(() => {
  return tryParseJsonObject(props.pageStaticConfig);
});

// JSON 对象存在且非空，才渲染 BaseIntroSection
const hasPageStaticConfigObject = computed(() => {
  const parsed = parsedPageStaticConfig.value;
  if (!parsed) return false;
  return Object.keys(parsed).length > 0;
});

// HTML 字符串形式的 pageStaticConfig（用于 v-html）
const htmlPageStaticConfig = computed(() => {
  const config = props.pageStaticConfig;
  if (!config) return "";

  // 如果是字符串，检查是否是 JSON 对象
  if (typeof config === "string") {
    const trimmed = config.trim();
    if (!trimmed) return "";

    // 如果能够解析为 JSON 对象，则不是 HTML
    if (tryParseJsonObject(config) !== null) {
      return "";
    }

    // 否则作为 HTML 返回
    return trimmed;
  }

  return "";
});

// HTML 字符串非空才渲染
const hasHtmlPageStaticConfig = computed(() => {
  return Boolean(htmlPageStaticConfig.value);
});

// ===== 公共部分 =====
// scrollToTop 支持参数: { smooth = true }，默认平滑滚动，传 { smooth: false } 可直接跳转
const scrollToTop = ({ smooth = true } = {}) => {
  if (typeof window === "undefined") return;
  const behavior = smooth ? "smooth" : "auto";
  console.log("scrollToTop called, smooth:", behavior);
  window.scrollTo({
    top: 0,
    behavior: behavior,
  });
};

// 通过 provide 将 scrollToTop 函数提供给子组件
provide("scrollToTop", scrollToTop);

// HTML 内容容器的 ref
const htmlContentRef = ref(null);

// 处理 HTML 内容中的点击事件（事件委托）
const handleHtmlContentClick = (event) => {
  const target = event.target;
  if (!target) return;

  // 检查是否有 data-scroll-to-top 属性
  if (target.hasAttribute("data-scroll-to-top")) {
    event.preventDefault();
    const smooth = target.getAttribute("data-scroll-to-top") !== "false";
    scrollToTop({ smooth });
    return;
  }

  // 检查是否有点击 scrollToTop 的标记（通过 class 或 data 属性）
  // 支持 class="scroll-to-top" 或 data-action="scrollToTop"
  if (
    target.classList.contains("scroll-to-top") ||
    target.getAttribute("data-action") === "scrollToTop" ||
    target.closest(".scroll-to-top") ||
    target.closest("[data-action='scrollToTop']")
  ) {
    event.preventDefault();
    scrollToTop();
    return;
  }

  // 检查 onclick 属性中是否包含 scrollToTop（兼容旧代码）
  const onclickAttr = target.getAttribute("onclick");
  if (onclickAttr && onclickAttr.includes("scrollToTop")) {
    event.preventDefault();
    scrollToTop();
    return;
  }
};

// 在组件挂载后绑定事件
onMounted(() => {
  nextTick(() => {
    if (htmlContentRef.value) {
      htmlContentRef.value.addEventListener("click", handleHtmlContentClick);
    }

    // 将 scrollToTop 挂载到 window 对象，方便 HTML 中使用 onclick="scrollToTop()"
    if (typeof window !== "undefined") {
      window.scrollToTop = scrollToTop;
    }
  });
});

// 在组件卸载前清理事件
onUnmounted(() => {
  if (htmlContentRef.value) {
    htmlContentRef.value.removeEventListener("click", handleHtmlContentClick);
  }

  // 清理全局函数
  if (typeof window !== "undefined" && window.scrollToTop) {
    delete window.scrollToTop;
  }
});

const cdnUrl = useRuntimeConfig().public.cdnUrl;
useHead({
  title: props.seoTitle,
  link: [
    {
      rel: "canonical",
      href: props.seoUrl,
    },
  ],
  meta: [
    {
      hid: "description",
      name: "description",
      content: props.seoDescription,
    },
    {
      hid: "og:site_name",
      property: "og:site_name",
      content: "SharkFoto",
    },
    {
      hid: "og:type",
      property: "og:type",
      content: "website",
    },
    {
      hid: "og:locale",
      property: "og:locale",
      content: "en_US",
    },
    {
      hid: "og:image",
      property: "og:image",
      content: `${cdnUrl}/sharkfoto_og.png`,
    },
    {
      hid: "og:url",
      property: "og:url",
      content: props.seoUrl,
    },
    {
      hid: "og:title",
      property: "og:title",
      content: props.seoTitle,
    },
    {
      hid: "og:description",
      property: "og:description",
      content: props.seoDescription,
    },
    {
      hid: "twitter:card",
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      hid: "twitter:url",
      name: "twitter:url",
      content: props.seoUrl,
    },
    {
      hid: "twitter:title",
      name: "twitter:title",
      content: props.seoTitle,
    },
    {
      hid: "twitter:image",
      name: "twitter:image",
      content: `${cdnUrl}/sharkfoto_og.png`,
    },
    {
      hid: "twitter:site",
      name: "twitter:site",
      content: "@Shark_Foto",
    },
    {
      hid: "twitter:description",
      name: "twitter:description",
      content: props.seoDescription,
    },
  ],
});
</script>

<style scoped>
/* 可放置全页的基础样式 */
.page-root {
  min-height: 89vh;
}
.main-content {
  margin: 0 auto;
}
</style>
